(() => {
  const productFallback = {
    "little-beaks": "Little Beaks",
    "hen-de-la-creme": "Hen De La Creme",
    "flock-and-awe": "Flock & Awe",
    "scratch-of-the-day": "Scratch of the Day",
    "cottontail-crunch": "Cottontail Crunch",
    "dream-goat-pellets": "Dream Goat Pellets",
    "piggie-smalls": "Piggie Smalls"
  };

  const animalLabels = {
    chickens: "chickens",
    rabbits: "rabbits",
    goats: "goats",
    "mini pigs": "mini pigs"
  };

  function initQuiz(root) {
    const steps = Array.from(root.querySelectorAll("[data-feed-step]"));
    const progress = root.querySelector("[data-feed-progress]");
    const productsScript = root.querySelector("[data-feed-products]");
    const products = productsScript ? JSON.parse(productsScript.textContent) : {};
    const klaviyoCompanyId = root.dataset.klaviyoCompanyId;
    const klaviyoListId = root.dataset.klaviyoListId;
    const state = {};

    function showStep(name) {
      steps.forEach((step) => {
        step.classList.toggle("is-active", step.dataset.feedStep === name);
      });

      const index = Math.max(0, steps.findIndex((step) => step.dataset.feedStep === name));
      if (progress) {
        progress.style.width = `${Math.max(12, ((index + 1) / steps.length) * 100)}%`;
      }
    }

    function setAnimalKicker() {
      const kicker = root.querySelector("[data-feed-animal-kicker]");
      const animal = animalLabels[state.animal] || "animals";
      if (kicker) {
        kicker.textContent = `For your ${animal}:`;
      }
    }

    function setResult() {
      const product = products[state.product] || {};
      const name = product.name || productFallback[state.product] || "Feather & Tail Feed";
      const nameTarget = root.querySelector("[data-feed-result-name]");
      const reasonTarget = root.querySelector("[data-feed-result-reason]");
      const linkTarget = root.querySelector("[data-feed-result-link]");

      if (nameTarget) {
        nameTarget.textContent = name;
      }

      if (reasonTarget) {
        reasonTarget.textContent = product.reason || "";
      }

      if (linkTarget) {
        linkTarget.textContent = `Shop ${name}`;
        linkTarget.href = product.url || "/collections/shop";
      }
    }

    function buildKlaviyoProperties(productName, productUrl, consent) {
      return {
        feed_finder_animal: state.animal || "",
        feed_finder_flock: state.flock || "",
        feed_finder_preference: state.preference || "",
        feed_finder_match: productName,
        feed_finder_match_url: productUrl,
        feed_finder_consent: consent
      };
    }

    async function subscribeToKlaviyo(email, properties) {
      if (!klaviyoCompanyId || !klaviyoListId) {
        return;
      }

      const response = await fetch(`https://a.klaviyo.com/client/subscriptions?company_id=${encodeURIComponent(klaviyoCompanyId)}`, {
        method: "POST",
        headers: {
          "accept": "application/vnd.api+json",
          "content-type": "application/vnd.api+json",
          "revision": "2026-07-15"
        },
        body: JSON.stringify({
          data: {
            type: "subscription",
            attributes: {
              custom_source: "Feed Finder Quiz",
              profile: {
                data: {
                  type: "profile",
                  attributes: {
                    email,
                    properties,
                    subscriptions: {
                      email: {
                        marketing: {
                          consent: "SUBSCRIBED"
                        }
                      }
                    }
                  }
                }
              }
            },
            relationships: {
              list: {
                data: {
                  type: "list",
                  id: klaviyoListId
                }
              }
            }
          }
        })
      });

      if (!response.ok) {
        const message = await response.text();
        throw new Error(message || "Klaviyo subscription request failed.");
      }
    }

    async function sendToKlaviyo(email, consent) {
      const product = products[state.product] || {};
      const productName = product.name || productFallback[state.product] || state.product;
      const productUrl = product.url || "";
      const properties = buildKlaviyoProperties(productName, productUrl, consent);
      const payload = {
        $email: email,
        ...properties
      };

      window._learnq = window._learnq || [];
      window._learnq.push(["identify", payload]);
      window._learnq.push(["track", "Feed Finder Quiz Completed", {
        animal: state.animal || "",
        flock: state.flock || "",
        preference: state.preference || "",
        recommended_product: productName,
        recommended_product_url: productUrl,
        consent
      }]);

      await subscribeToKlaviyo(email, properties);
    }

    root.addEventListener("click", (event) => {
      const target = event.target.closest("[data-feed-next], [data-feed-reset]");
      if (!target || !root.contains(target)) {
        return;
      }

      if (target.matches("[data-feed-reset]")) {
        Object.keys(state).forEach((key) => {
          delete state[key];
        });
        showStep("intro");
        return;
      }

      if (target.dataset.feedAnimal) {
        state.animal = target.dataset.feedAnimal;
      }

      if (target.dataset.feedFlock) {
        state.flock = target.dataset.feedFlock;
      }

      if (target.dataset.feedProduct) {
        state.product = target.dataset.feedProduct;
      }

      if (target.dataset.feedPreference) {
        state.preference = target.dataset.feedPreference;
      }

      if (target.dataset.feedNext === "preference") {
        setAnimalKicker();
      }

      showStep(target.dataset.feedNext);
    });

    const form = root.querySelector("[data-feed-email-form]");
    if (form) {
      form.addEventListener("submit", async (event) => {
        event.preventDefault();
        const error = root.querySelector("[data-feed-error]");
        const submitButton = form.querySelector("button[type='submit']");
        const email = form.email.value.trim();
        const consent = form.consent.checked;

        if (!form.email.validity.valid) {
          if (error) {
            error.textContent = "Enter a valid email to see your match.";
          }
          form.email.focus();
          return;
        }

        if (!consent) {
          if (error) {
            error.textContent = "Check the consent box to continue.";
          }
          form.consent.focus();
          return;
        }

        if (error) {
          error.textContent = "";
        }

        if (submitButton) {
          submitButton.disabled = true;
          submitButton.textContent = "Getting your match...";
        }

        try {
          await sendToKlaviyo(email, consent);
        } catch (subscriptionError) {
          console.error(subscriptionError);
        } finally {
          setResult();
          showStep("result");
          if (submitButton) {
            submitButton.disabled = false;
            submitButton.textContent = "Show my match";
          }
        }
      });
    }

    showStep("intro");
  }

  document.querySelectorAll("[data-feed-finder]").forEach(initQuiz);
})();
